import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { BarreEvenement } from './BarreEvenement.js';
import { ElementEvenement } from './ElementEvenement.js';
import {
  barresDeSemaine,
  formaterJour,
  heureDeMinutes,
  joursDeLaSemaine,
  plageDeVue,
  segmentsALHeureParJour,
  semainesDuMois,
} from './periodes.js';
import type { PropsVue } from './types.js';
import styles from './Calendrier.module.css';

/** Au-delà, les rendez-vous d'un jour se résument en « + N autres » (vers la semaine). */
const PASTILLES_PAR_JOUR = 3;

export function VueMois({
  ancre,
  evenements,
  aujourdhui,
  langue,
  onAller,
  onCreer,
}: PropsVue): JSX.Element {
  const { t } = useTranslation('regie');
  const semaines = semainesDuMois(ancre);
  const { du, au } = plageDeVue('mois', ancre);
  const parJour = segmentsALHeureParJour(evenements, du, au);
  const mois = ancre.slice(0, 7);

  return (
    <div
      className={styles.mois}
      data-ui="calendrier-mois"
      aria-label={t('calendrier.grille.mois')}
      role="group"
    >
      <div className={styles.entetesJours} aria-hidden>
        {joursDeLaSemaine(ancre).map((jour) => (
          <span key={jour}>{formaterJour(jour, langue, { weekday: 'short' })}</span>
        ))}
      </div>
      {semaines.map((jours) => {
        const barres = barresDeSemaine(evenements, jours[0]!);
        const voies = barres.reduce((max, barre) => Math.max(max, barre.voie + 1), 0);
        return (
          <div
            key={jours[0]}
            className={styles.semaineMois}
            // Ligne 1 : les numéros ; puis une ligne par voie de barres ; puis les pastilles.
            style={{ gridTemplateRows: `auto${' auto'.repeat(voies)} 1fr` }}
          >
            {jours.map((jour, colonne) => (
              <div
                key={`fond-${jour}`}
                className={styles.cellule}
                style={{ gridColumn: colonne + 1, gridRow: '1 / -1' }}
                data-jour={jour}
                data-hors-mois={jour.slice(0, 7) === mois ? undefined : 'oui'}
                data-aujourdhui={jour === aujourdhui ? 'oui' : undefined}
                data-creable={onCreer ? 'oui' : undefined}
                onClick={onCreer ? () => onCreer(jour) : undefined}
              />
            ))}
            {jours.map((jour, colonne) => (
              <button
                key={`numero-${jour}`}
                type="button"
                className={styles.numero}
                style={{ gridColumn: colonne + 1, gridRow: 1 }}
                data-aujourdhui={jour === aujourdhui ? 'oui' : undefined}
                data-hors-mois={jour.slice(0, 7) === mois ? undefined : 'oui'}
                aria-label={t('calendrier.voirSemaine', {
                  date: formaterJour(jour, langue, {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                  }),
                })}
                onClick={() => onAller('semaine', jour)}
              >
                {Number(jour.slice(8))}
              </button>
            ))}
            {barres.map((barre) => (
              <BarreEvenement key={barre.evenement.id} barre={barre} premiereLigne={2} />
            ))}
            {jours.map((jour, colonne) => {
              const segments = parJour.get(jour) ?? [];
              if (!segments.length) return null;
              const visibles = segments.slice(0, PASTILLES_PAR_JOUR);
              const reste = segments.length - visibles.length;
              return (
                <ul
                  key={`pastilles-${jour}`}
                  className={styles.pastilles}
                  style={{ gridColumn: colonne + 1, gridRow: voies + 2 }}
                  aria-label={formaterJour(jour, langue, { day: 'numeric', month: 'long' })}
                >
                  {visibles.map((segment) => (
                    <li
                      key={`${segment.evenement.id}-${jour}`}
                      className={styles.pastille}
                      data-teinte={segment.evenement.teinte}
                      data-evidence={segment.evenement.evidence ? 'oui' : undefined}
                    >
                      <ElementEvenement
                        evenement={segment.evenement}
                        className={styles.lienPastille}
                        titre={segment.evenement.titre}
                      >
                        <span className={styles.point} aria-hidden />
                        <span className={styles.heurePastille}>
                          {segment.debutCoupe ? '…' : heureDeMinutes(segment.debutMinute)}
                        </span>
                        <span className={styles.textePastille}>{segment.evenement.titre}</span>
                      </ElementEvenement>
                    </li>
                  ))}
                  {reste > 0 ? (
                    <li>
                      <button
                        type="button"
                        className={styles.plus}
                        onClick={() => onAller('semaine', jour)}
                      >
                        {t('calendrier.plus', { count: reste })}
                      </button>
                    </li>
                  ) : null}
                </ul>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
